// -----------------------------------------------------------------------------
// Theme — Couleurs (tokens) + CSS global
// StockPro / VisualisPro
// -----------------------------------------------------------------------------

/**
 * Retourne les couleurs du thème selon le mode (sombre/clair).
 * L'accent par défaut est l'orange VisualisPro, mais peut être surchargé
 * par la couleur personnalisée du userProfile.
 *
 * @param {boolean} dark - true = mode sombre, false = mode clair
 * @returns {object} Tokens de couleurs
 */
export const getColors = (dark) => ({
  bg: dark ? "#070711" : "#f0f2f8",
  cardBg: dark ? "rgba(255,255,255,.03)" : "#ffffff",
  textPrimary: dark ? "#e2e8f0" : "#1e293b",
  textSecondary: dark ? "#666" : "#888",
  borderColor: dark ? "rgba(255,255,255,.07)" : "rgba(0,0,0,.08)",
  inputBg: dark ? "rgba(255,255,255,.05)" : "#f8fafc",
  inputBorder: dark ? "rgba(255,255,255,.1)" : "rgba(0,0,0,.1)",
  hoverBg: dark ? "rgba(255,255,255,.05)" : "rgba(0,0,0,.04)",
  scrollTrack: dark ? "#111" : "#e5e7eb",
  scrollThumb: dark ? "#2a2a3a" : "#cbd5e1",
});

/**
 * Couleur d'accent par défaut (orange VisualisPro).
 */
export const DEFAULT_ACCENT = "#FF6B35";

/**
 * Génère le CSS global de l'application en fonction du mode sombre/clair.
 * À injecter via <style>{buildCSS(dark)}</style> au sein du composant racine.
 *
 * @param {boolean} dark - true = mode sombre
 * @returns {string} Feuille de style CSS
 */
export const buildCSS = (dark) => {
  const c = getColors(dark);
  return `
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&family=Cairo:wght@400;600;700;800&family=JetBrains+Mono:wght@400;600&display=swap');
*{box-sizing:border-box;margin:0;padding:0;}
body{font-family:'Outfit',sans-serif;background:${c.bg};}
::-webkit-scrollbar{width:4px;}
::-webkit-scrollbar-track{background:${c.scrollTrack};}
::-webkit-scrollbar-thumb{background:${c.scrollThumb};border-radius:2px;}
button,input,select,textarea{font-family:inherit;border:none;outline:none;}
button{cursor:pointer;}
[dir=rtl]{font-family:'Cairo',sans-serif;}
@keyframes fadeUp{from{opacity:0;transform:translateY(14px);}to{opacity:1;transform:translateY(0);}}
@keyframes fadeIn{from{opacity:0;}to{opacity:1;}}
@keyframes spin{to{transform:rotate(360deg);}}
@keyframes pulse{0%,100%{opacity:1;}50%{opacity:.4;}}
@keyframes slideIn{from{opacity:0;transform:translateX(12px);}to{opacity:1;transform:translateX(0);}}
@keyframes toast{from{opacity:0;transform:translateY(10px);}to{opacity:1;transform:translateY(0);}}
.fade-up{animation:fadeUp .35s ease forwards;}
.slide-in{animation:slideIn .3s ease forwards;}
.card{background:${c.cardBg};border:1px solid ${c.borderColor};border-radius:14px;}
.nav-btn{display:flex;align-items:center;gap:9px;padding:9px 11px;border-radius:9px;font-size:13px;font-weight:500;color:${c.textSecondary};width:100%;transition:.15s;background:none;border:none;}
.nav-btn:hover{background:${c.hoverBg};color:${dark ? "#aaa" : "#555"};}
.fi{width:100%;padding:10px 13px;background:${c.inputBg};border:1px solid ${c.inputBorder};border-radius:9px;color:${c.textPrimary};font-size:14px;transition:.2s;}
.fi:focus{border-color:rgba(255,107,53,.5);}
.fi option{background:${dark ? "#1a1a2e" : "#ffffff"};color:${c.textPrimary};}
.modal-bg{position:fixed;inset:0;background:rgba(0,0,0,.75);display:flex;align-items:center;justify-content:center;z-index:100;animation:fadeIn .2s;padding:16px;overflow-y:auto;}
.qbtn{width:36px;height:36px;border-radius:9px;display:flex;align-items:center;justify-content:center;font-size:20px;font-weight:700;transition:.15s;border:none;cursor:pointer;}
.qbtn:hover{transform:scale(1.1);}
.tag{padding:3px 9px;border-radius:20px;font-size:11px;font-weight:600;}
.glow-btn{transition:.2s;}
.glow-btn:hover{transform:translateY(-1px);box-shadow:0 8px 25px rgba(255,107,53,.3);}
`;
};
